import { useMutation, useQueryClient } from '@tanstack/vue-query';

// Toggles watching on a developer. Optimistically updates the cached
// watched list so the UI flips instantly; reverts on error.
export const useToggleWatchDeveloper = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ developerId, watching }: { developerId: number; watching: boolean }) => {
      const method = watching ? 'POST' : 'DELETE';
      return $fetch<{ success: boolean; watching: boolean }>(
        `/api/developers/by-id/${developerId}/watch`,
        { method, headers: { Authorization: `Bearer ${authStore.token}` } },
      );
    },
    onMutate: async ({ developerId, watching }) => {
      await queryClient.cancelQueries({ queryKey: ['developers', 'watched'] });
      const previous = queryClient.getQueryData<{ data: number[] }>(['developers', 'watched']);
      const prevIds = previous?.data ?? [];
      const nextIds = watching
        ? prevIds.includes(developerId) ? prevIds : [...prevIds, developerId]
        : prevIds.filter((id) => id !== developerId);
      queryClient.setQueryData(['developers', 'watched'], { success: true, data: nextIds });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['developers', 'watched'], context.previous);
      }
      // Surface failures instead of silently reverting the optimistic flip.
      useErrorHandler().catchErrors(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['developers', 'watched'] });
    },
  });
};
